import { Check, LifeBuoy, Loader2, LogOut, Plus } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../common/helpers/cn';
import { getColor, getTextColorForBg } from '../../common/helpers/color';
import useStudentSubscriptionLabel from '../../common/hooks/useStudentSubscriptionLabel';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown';
import { useSidebar } from '../../components/ui/sidebar';
import { Skeleton } from '../../components/ui/skeleton';
import { useAccountSwitcher } from './useAccountSwitcher';

const AVATAR_PROFILE_COLORS = [
  'bg-avatar-profile-purple',
  'bg-avatar-profile-green',
  'bg-avatar-profile-yellow',
  'bg-avatar-profile-magenta',
  'bg-avatar-profile-blue',
  'bg-avatar-profile-violet',
  'bg-avatar-profile-turquoise',
  'bg-avatar-profile-red',
  'bg-avatar-profile-amber',
];

const LIGHT_BACKGROUND_COLORS = ['bg-avatar-profile-yellow', 'bg-avatar-profile-amber'];

type AccountSwitcherProps = {
  onNavigate?: () => void;
};

export function AccountSwitcher({ onNavigate }: AccountSwitcherProps) {
  const {
    accountHolder,
    students,
    isLoading,
    selectingStudentId,
    signOut,
    onSelectStudent,
    onAddStudent,
    onSupport,
    isAddStudentActive,
    isSupportActive,
  } = useAccountSwitcher();
  const { getStudentSubscriptionLabel } = useStudentSubscriptionLabel(students);
  const { open: sidebarOpen, isMobile } = useSidebar();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-12 items-center gap-3 px-2">
        <Skeleton className="h-8 w-8 rounded-full bg-muted-foreground" />
        <div className="flex flex-1 flex-col gap-1">
          <Skeleton className="h-4 w-24 bg-muted-foreground" />
          <Skeleton className="h-3 w-16 bg-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!accountHolder) {
    return null;
  }

  const accountColor = getColor(accountHolder.firstName + accountHolder.lastName, AVATAR_PROFILE_COLORS);
  const accountInitials = `${accountHolder.firstName.charAt(0)}${accountHolder.lastName.charAt(0)}`;

  return (
    <div className="relative isolate overflow-visible">
      {selectingStudentId &&
        createPortal(
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-background/80 backdrop-blur-sm"
            data-testid="student-sso-loading-backdrop"
            aria-busy="true"
            aria-live="polite"
          >
            <Loader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
            <span className="sr-only">Opening student hub</span>
          </div>,
          document.body
        )}
      <DropdownMenu
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        // The mobile sidebar is already a modal Sheet. A modal menu stacked on
        // it leaves the dismissable layer in place after the first student
        // select, so the next select no longer dismisses the drawer.
        modal={!isMobile}
      >
        <DropdownMenuTrigger asChild className="cursor-pointer">
          <Button
            variant="outline"
            size={sidebarOpen || isMobile ? 'lg' : 'icon'}
            data-testid="account-switcher-trigger"
            className={cn(
              'relative z-20 shrink-0 rounded-full border border-white hover:border hover:border-grey-200 hover:bg-white data-[state=open]:bg-white data-[state=open]:shadow-lg',
              sidebarOpen || isMobile ? 'h-auto w-full max-w-[250px] px-2 py-2' : 'h-10 w-10 p-0',
              dropdownOpen ? 'border-brown-300 !bg-brown-300' : 'bg-white'
            )}
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={undefined} alt={undefined} />
              <AvatarFallback className="bg-transparent">
                <div
                  className={cn(
                    'flex aspect-auto h-10 w-10 items-center justify-center rounded-full text-sm font-semibold',
                    accountColor,
                    getTextColorForBg(accountColor, LIGHT_BACKGROUND_COLORS)
                  )}
                >
                  {accountInitials}
                </div>
              </AvatarFallback>
            </Avatar>
            {(sidebarOpen || isMobile) && (
              <div className="ml-2 grid min-w-0 flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {accountHolder.firstName} {accountHolder.lastName}
                </span>
                <span className="truncate text-sm font-normal text-grey-400">{accountHolder.role}</span>
              </div>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          // On mobile the sidebar is a Radix Dialog (Sheet); portaling to body
          // would drop the menu outside the modal's focus scope + inert region
          // and behind its overlay. Render inline so it stays inside the Sheet.
          disablePortal={isMobile}
          onCloseAutoFocus={(event) => {
            // Returning focus to the trigger (inside the sheet) runs while the
            // drawer is closing and cancels that close on the next student select.
            if (isMobile) event.preventDefault();
          }}
          className={cn(
            'z-10 flex max-h-[500px] flex-col overflow-hidden rounded-2xl border-0 bg-white pt-3 shadow-xl',
            sidebarOpen || isMobile ? 'w-[var(--radix-dropdown-menu-trigger-width)]' : 'w-[300px]'
          )}
          align="start"
          side="top"
          sideOffset={8}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            <DropdownMenuLabel className="mb-1 py-0 font-normal text-grey-400">Account</DropdownMenuLabel>
            <DropdownMenuItem className="mx-1 cursor-default gap-3 rounded-full bg-blue-100">
              <Avatar className="h-8 w-8">
                <AvatarImage src={undefined} alt={undefined} />
                <AvatarFallback className="bg-transparent">
                  <div
                    className={cn(
                      'flex aspect-auto h-8 w-8 items-center justify-center rounded-full text-xs font-semibold',
                      accountColor,
                      getTextColorForBg(accountColor, LIGHT_BACKGROUND_COLORS)
                    )}
                  >
                    {accountInitials}
                  </div>
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate text-sm font-semibold">
                  {accountHolder.firstName} {accountHolder.lastName}
                </span>
                <span className="truncate text-xs text-grey-400">{accountHolder.role}</span>
              </div>
              <Check className="ml-auto h-4 w-4" />
            </DropdownMenuItem>
            {students.length > 0 && (
              <>
                <DropdownMenuLabel className="mb-1 font-normal text-muted-foreground">Student</DropdownMenuLabel>
                <div className="space-y-1">
                  {students.map((student) => {
                    const color = getColor((student.firstName ?? '') + (student.lastName ?? ''), AVATAR_PROFILE_COLORS);
                    const isSelecting = selectingStudentId === student.studentId;
                    const subscriptionLabel = getStudentSubscriptionLabel(student.studentId);
                    return (
                      <DropdownMenuItem
                        key={student.studentId}
                        onSelect={() => {
                          // Close the menu before the sheet. Unmounting the sheet
                          // while this layer is still open sticks the drawer open
                          // on the next student select.
                          setDropdownOpen(false);
                          onSelectStudent(student);
                          onNavigate?.();
                        }}
                        className="mx-1 cursor-pointer gap-3 rounded-full hover:bg-brown-100"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={undefined} alt={undefined} />
                          <AvatarFallback className="bg-transparent">
                            <div
                              className={cn(
                                'flex aspect-auto h-8 w-8 items-center justify-center rounded-full text-xs font-semibold',
                                color,
                                getTextColorForBg(color, LIGHT_BACKGROUND_COLORS)
                              )}
                            >
                              {student.firstName?.charAt(0)}
                            </div>
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate text-sm font-medium">{student.firstName}</span>
                          <span className="truncate text-xs text-muted-foreground">{student.yearLevel}</span>
                        </div>
                        {isSelecting ? (
                          <Loader2 className="ml-auto h-4 w-4 shrink-0 animate-spin" aria-label="Loading" />
                        ) : (
                          subscriptionLabel && (
                            <span
                              className={cn(
                                'ml-auto shrink-0 whitespace-nowrap rounded-full px-2 py-1 text-xs font-semibold text-grey-600',
                                subscriptionLabel.className
                              )}
                            >
                              {subscriptionLabel.text}
                            </span>
                          )
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              </>
            )}
            <div className="mr-2">
              <DropdownMenuSeparator className="my-2" />
              <DropdownMenuItem asChild>
                <button
                  onClick={onAddStudent}
                  className={cn(
                    'mx-1 flex h-10 w-full cursor-pointer items-center gap-3 rounded-xl p-4 hover:bg-muted',
                    isAddStudentActive && 'bg-brown-200'
                  )}
                >
                  <Plus className="h-5 w-5" />
                  <span className="text-sm leading-5">Add student</span>
                </button>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-2" />
              <DropdownMenuItem asChild>
                <button
                  onClick={onSupport}
                  className={cn(
                    'mx-1 flex h-10 w-full cursor-pointer items-center gap-3 rounded-xl p-4 hover:bg-muted',
                    isSupportActive && 'bg-brown-200'
                  )}
                >
                  <LifeBuoy className="h-5 w-5" />
                  <span className="text-sm leading-5">Support</span>
                </button>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-2" />
              <DropdownMenuItem asChild>
                <button
                  onClick={signOut}
                  className="mx-1 mb-2 flex h-10 w-full cursor-pointer items-center gap-3 rounded-xl p-4 hover:bg-muted"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="text-sm leading-5">Log out</span>
                </button>
              </DropdownMenuItem>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
